import { PersistentTelemetryDataContext, useLogger } from "@amzn/xb-hooks";
import { useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { recordUrl } from "../stores/PageHistoryStore";
import { useAWSMA } from "./useAWSMA";

declare global {
  interface Window {
    s: any;
  }
}

const DL_PAGEVIEW = {
  calls: 0,
};

// exported for testing purposes only
export const resetDlPageview = () => {
  DL_PAGEVIEW.calls = 0;
};

/**
 * Initializes the PageHistoryStore and tracks page updates in the history in order to emit "customPageViews".
 *
 * https://w.amazon.com/bin/view/AWS_Marketing/DSE/MetricsEngineering/AnalyticsSCode/ImplementSCode#HIfyouneedtotriggerapageviewcallonaSPA
 *
 * This hook is intended to be used as a singleton in the root of the app, but there is no mechanism
 * currently enforcing that. However, limited testing demonstrates that it is idempotent-ish™.
 */
export const usePageLevelTelemetry = () => {
  const { data, isReadyToEmit } = useContext(PersistentTelemetryDataContext);
  const { pathname, search } = useLocation();
  const { EVENT_NAME, ready } = useAWSMA();
  const { info, debug } = useLogger();
  const pageURL = useMemo(() => `${window.location.origin}${pathname}${search}`, [pathname, search]);

  debug("Rendering usePageLevelTelemetry", isReadyToEmit, data);

  /**
   * SPA level page tracking via Telemetry
   * @see https://tiny.amazon.com/a1t31ran/s_codeforSPA
   */

  const isNewUrl = recordUrl(pageURL);
  if (isNewUrl) {
    const detail = {
      page: {
        pageURL,
      },
      event: {
        type: "pageview",
        name: `${pathname} page load`,
      },
    };
    ready(() => {
      info("Dispatching pageview event", detail);
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
    });
  }

  // firing off the 'dlpageview' event as prescribed in https://quip-amazon.com/L8FdA45XAqLP
  // for now we ensure that we fire dlpageview exactly once when isReadyToEmit is fired.
  if (isReadyToEmit && DL_PAGEVIEW.calls === 0) {
    DL_PAGEVIEW.calls += 1;
    const detail = {
      event: {
        type: "dlPageview", // Required.
        name: "Data layer pageview", // Required.
      },
      data,
    };
    ready(() => {
      info("Dispatching dlpageview event", detail);
      document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail }));
    });
  }
};
